import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  TrophyIcon,
  FlagIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import ChatPanel from '../components/ChatPanel'
import { useChatApi, type ChatThread, type ChatThreadKind } from '../services/chatApi'

function kindIcon(kind: ChatThreadKind) {
  switch (kind) {
    case 'event':
      return CalendarIcon
    case 'pool':
      return TrophyIcon
    case 'outing':
      return FlagIcon
    default:
      return UserGroupIcon
  }
}

function formatListTime(iso: string) {
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    return sameDay
      ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

function placeholderFor(kind: ChatThreadKind) {
  switch (kind) {
    case 'event':
      return 'Message this event…'
    case 'pool':
      return 'Message this pool…'
    case 'outing':
      return 'Message this outing…'
    default:
      return 'Message all Railbirds…'
  }
}

export default function ChatPage() {
  const chatApi = useChatApi()
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [mobileShowThread, setMobileShowThread] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadThreads = async () => {
    try {
      setLoading(true)
      const list = await chatApi.listThreads()
      setThreads(list)
      setError(null)
      setSelectedId((prev) => {
        if (prev && list.some((t) => t.threadId === prev || t.id === prev)) return prev
        return list[0]?.threadId || list[0]?.id || ''
      })
    } catch (err: any) {
      setError(err?.message || 'Failed to load chats')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadThreads()
    const id = window.setInterval(() => {
      chatApi.listThreads().then(setThreads).catch(() => {})
    }, 15000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selected = useMemo(
    () => threads.find((t) => t.threadId === selectedId || t.id === selectedId) || threads[0],
    [threads, selectedId]
  )

  const selectThread = (thread: ChatThread) => {
    setSelectedId(thread.threadId || thread.id)
    setMobileShowThread(true)
    // Opening a thread marks it read via GET messages; clear badge immediately in UI
    setThreads((prev) =>
      prev.map((t) =>
        (t.threadId || t.id) === (thread.threadId || thread.id) ? { ...t, unread: 0 } : t
      )
    )
  }

  const handleMuteChange = (muted: boolean) => {
    if (!selected) return
    const id = selected.threadId || selected.id
    setThreads((prev) =>
      prev.map((t) => ((t.threadId || t.id) === id ? { ...t, muted } : t))
    )
  }

  return (
    <div className="h-full flex flex-col bg-rb-dark">
      <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-white/10 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-white truncate">Chat</h1>
          <p className="text-xs text-white/70 truncate">
            All Railbirds, events, pools, and outings you’re in
          </p>
        </div>
        <p className="hidden sm:block text-xs text-white/60 flex-shrink-0">
          {threads.length} thread{threads.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="flex-1 min-h-0 flex border-t border-white/10 bg-white dark:bg-gray-800 overflow-hidden">
        <aside
          className={`${
            mobileShowThread ? 'hidden' : 'flex'
          } lg:flex w-full lg:w-80 xl:w-[22rem] flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50`}
        >
          <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Your threads</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && threads.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
            )}
            {error && (
              <div className="px-4 py-4 text-sm text-red-600 dark:text-red-400">
                <p>{error}</p>
                <button type="button" onClick={loadThreads} className="mt-2 underline font-medium">
                  Retry
                </button>
              </div>
            )}
            {!loading && !error && threads.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500">No chats yet.</p>
            )}
            {threads.map((thread) => {
              const Icon = kindIcon(thread.kind)
              const active = (thread.threadId || thread.id) === (selected?.threadId || selected?.id)
              return (
                <button
                  key={thread.threadId || thread.id}
                  type="button"
                  onClick={() => selectThread(thread)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-200 dark:border-gray-700 transition-colors ${
                    active
                      ? 'bg-primary-50 dark:bg-primary-900/30'
                      : 'hover:bg-white dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                        active
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {thread.title}
                          {thread.muted ? (
                            <span className="ml-1 text-[10px] font-normal text-gray-400">Muted</span>
                          ) : null}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatListTime(thread.updatedAt)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{thread.subtitle}</p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        {thread.preview ? (
                          <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                            {thread.preview}
                          </p>
                        ) : (
                          <span />
                        )}
                        {thread.unread && thread.unread > 0 ? (
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-primary-600 text-white">
                            {thread.unread > 99 ? '99+' : thread.unread}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </aside>

        <section
          className={`${
            mobileShowThread ? 'flex' : 'hidden'
          } lg:flex flex-1 flex-col min-w-0 min-h-0 bg-white dark:bg-gray-800`}
        >
          {selected ? (
            <>
              <div className="lg:hidden flex-shrink-0 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setMobileShowThread(false)}
                  className="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-1" />
                  All chats
                </button>
              </div>

              <div className="flex-1 min-h-0">
                <ChatPanel
                  key={selected.threadId || selected.id}
                  kind={selected.kind}
                  entityId={selected.kind === 'club' ? undefined : selected.entityId}
                  title={selected.title}
                  subtitle={selected.subtitle}
                  placeholder={placeholderFor(selected.kind)}
                  embedded
                  className="h-full"
                  muted={Boolean(selected.muted)}
                  onMuteChange={handleMuteChange}
                />
              </div>

              {selected.kind !== 'club' && selected.path && (
                <div className="flex-shrink-0 px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                  Also available from the{' '}
                  <Link to={selected.path} className="underline hover:text-primary-600">
                    dedicated thread page
                  </Link>
                  .
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16 text-gray-500 dark:text-gray-400">
              <ChatBubbleLeftRightIcon className="w-10 h-10 mb-3 opacity-50" />
              <p>Select a thread to start chatting.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
